import {
  PARCOURS_TYPES_RESTRICTED,
  getFilteredParcoursOptions,
  getParcoursConfig,
  getTemplatesForType,
  getTypeEvalLabel,
} from "@/lib/parcoursConfig";
import type { EvalConfig, TypeParcoursKey } from "@/lib/parcoursConfig";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { buildParcoursDescription } from "@repo/utils";
import { useMutation } from "convex/react";
import { ChevronDown, ChevronUp, Loader2, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../../../../convex/_generated/api";
import { CsvImportModal, type CsvCandidat } from "./CsvImportModal";

const parcoursOptions = getFilteredParcoursOptions();
const defaultParcoursType = (parcoursOptions[0]?.value ?? "recrutement") as TypeParcoursKey;
const hideParcoursTypeSelector =
  parcoursOptions.length === 1 &&
  getParcoursConfig(defaultParcoursType).evaluations.every((e) => !!e.template);

interface CandidatRow {
  emailCandidat: string;
  nomCandidat: string;
  prenomCandidat: string;
  token: string;
}

export function CampagneCreator() {
  const [parcoursType, setParcoursType] = useState<TypeParcoursKey>(defaultParcoursType);
  const [nom, setNom] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [customEvaluations, setCustomEvaluations] = useState<EvalConfig[]>(
    () => [...getParcoursConfig(defaultParcoursType).evaluations],
  );
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [candidats, setCandidats] = useState<CandidatRow[]>([]);
  const [anonyme, setAnonyme] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    campagneId: string;
    nbParcours: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const creerCampagne = useMutation(api.campagnes.creerCampagne);

  const handleParcoursTypeChange = (value: TypeParcoursKey) => {
    setParcoursType(value);
    const config = getParcoursConfig(value);
    setCustomEvaluations([...config.evaluations]);
    setIsCustomizing(false);
  };

  const handleTemplateChange = (index: number, template: string) => {
    setIsCustomizing(true);
    setCustomEvaluations((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current) updated[index] = { ...current, template: template as EvalConfig["template"] };
      return updated;
    });
  };

  const genererToken = (index: number) => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const part = (n: number) =>
      Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    const newToken = `${part(4)}-${part(4)}`;
    setCandidats((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current) updated[index] = { ...current, token: newToken };
      return updated;
    });
  };

  const addCandidatRow = () => {
    setCandidats((prev) => [
      ...prev,
      { emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" },
    ]);
  };

  const removeCandidatRow = (index: number) => {
    setCandidats((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCandidat = (
    index: number,
    field: keyof CandidatRow,
    value: string,
  ) => {
    setCandidats((prev) => {
      const updated = [...prev];
      const current = updated[index];
      if (current) updated[index] = { ...current, [field]: value };
      return updated;
    });
  };

  const handleCsvImport = (csvCandidats: CsvCandidat[]) => {
    setCandidats((prev) => [
      ...prev.filter(
        (c) => c.emailCandidat || c.nomCandidat || c.prenomCandidat,
      ),
      ...csvCandidats.map((c) => ({ ...c, token: "" })),
    ]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]!.split(",").map((h) => h.trim().toLowerCase());
      const nomIdx = header.findIndex((h) => ["nom", "nomcandidat"].includes(h));
      const prenomIdx = header.findIndex((h) =>
        ["prenom", "prenomcandidat"].includes(h),
      );
      const emailIdx = header.findIndex((h) =>
        ["email", "emailcandidat"].includes(h),
      );
      const tokenIdx = header.findIndex((h) => h === "token");
      if (nomIdx === -1) return;

      const parsed: CandidatRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i]!.split(",").map((c) => c.trim());
        const nomVal = cols[nomIdx] || "";
        const prenomVal = prenomIdx !== -1 ? (cols[prenomIdx] || "") : "";
        const emailVal = emailIdx !== -1 ? (cols[emailIdx] || "") : "";
        const tokenVal = tokenIdx !== -1 ? (cols[tokenIdx] || "") : "";
        if (nomVal) {
          parsed.push({
            nomCandidat: nomVal,
            prenomCandidat: prenomVal,
            emailCandidat: emailVal,
            token: tokenVal,
          });
        }
      }
      if (parsed.length > 0) {
        setCandidats((prev) => [
          ...prev.filter(
            (c) => c.emailCandidat || c.nomCandidat || c.prenomCandidat || c.token,
          ),
          ...parsed,
        ]);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const baseConfig = getParcoursConfig(parcoursType);
      const configEvals = isCustomizing ? customEvaluations : baseConfig.evaluations;

      if (configEvals.length === 0) {
        throw new Error("Au moins une évaluation est requise");
      }

      const validCandidats = candidats.filter((c) =>
        anonyme
          ? c.token
          : c.emailCandidat && c.nomCandidat && c.prenomCandidat,
      );

      const response = await creerCampagne({
        nom: nom || undefined,
        typeParcours: parcoursType,
        evaluationsConfig: configEvals.map((ec) => ({
          typeEval: ec.typeEval,
          template: ec.template,
        })),
        dateDebut: dateDebut ? new Date(dateDebut).getTime() : undefined,
        dateFin: dateFin ? new Date(dateFin).getTime() : undefined,
        anonyme,
        candidats: validCandidats.length > 0
          ? validCandidats.map((c) => ({
              emailCandidat: c.emailCandidat,
              nomCandidat: anonyme ? c.token : c.nomCandidat,
              prenomCandidat: c.prenomCandidat,
              token: c.token || undefined,
            }))
          : undefined,
      });

      setResult({
        campagneId: response.campagneId,
        nbParcours: response.parcoursCrees.length,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full min-w-[33%] px-8 py-8">
      <div className="mb-6">
        <Link
          to="/campagnes"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Retour aux campagnes
        </Link>
      </div>

      <div className="grid gap-6 w-7xl">
        {result && (
          <Card className="border-green-500">
            <CardHeader>
              <CardTitle className="text-green-600">
                Campagne créée avec succès !
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 font-mono text-sm">
                <div>
                  <strong>Campagne ID:</strong> {result.campagneId}
                </div>
                <div>
                  <strong>Parcours créés:</strong> {result.nbParcours}
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to={`/?campagne=${result.campagneId}`}>
                    <Button variant="outline">Voir les parcours</Button>
                  </Link>
                  <Link to="/campagnes">
                    <Button variant="outline">Retour aux campagnes</Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-red-500">
            <CardHeader>
              <CardTitle className="text-red-600">Erreur</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm text-red-600 whitespace-pre-wrap">
                {error}
              </pre>
            </CardContent>
          </Card>
        )}

        <Card className="min-w-112.5">
          <CardHeader>
            <CardTitle>Créer une Campagne</CardTitle>
            <CardDescription>
              Configurez la campagne. Les candidats peuvent être ajoutés
              maintenant ou plus tard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-6">
              {/* Mode anonyme */}
              <div className="flex items-center gap-2">
                <input
                  id="anonyme"
                  type="checkbox"
                  checked={anonyme}
                  onChange={(e) => setAnonyme(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="anonyme">Campagne anonyme</Label>
              </div>

              {/* Nom + type parcours */}
              <div className="grid gap-2">
                <Label htmlFor="nom">Nom de la campagne (optionnel)</Label>
                <Input
                  id="nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Auto-généré : type - évaluations - date"
                />
              </div>

              {!hideParcoursTypeSelector && (
                <div className="grid gap-2">
                  <Label>Type de parcours</Label>
                  <Select
                    value={parcoursType}
                    onValueChange={(v) => handleParcoursTypeChange(v as TypeParcoursKey)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {parcoursOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {buildParcoursDescription(
                      (isCustomizing ? customEvaluations : getParcoursConfig(parcoursType).evaluations).map(
                        (e) => ({ typeEval: e.typeEval, template: e.template }),
                      ),
                    )}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="dateDebut">Date de début (optionnel)</Label>
                  <Input
                    id="dateDebut"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="dateFin">Date de fin (optionnel)</Label>
                  <Input
                    id="dateFin"
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                  />
                </div>
              </div>

              {/* Évaluations */}
              {PARCOURS_TYPES_RESTRICTED ? (
                (() => {
                  const currentConfig = getParcoursConfig(parcoursType);
                  const evalsToShow = customEvaluations
                    .map((evalConfig, index) => ({
                      evalConfig,
                      index,
                      hasFixedTemplate: !!currentConfig.evaluations[index]?.template,
                    }))
                    .filter(({ hasFixedTemplate }) => !hasFixedTemplate);

                  if (evalsToShow.length === 0) return null;

                  return (
                    <div className="border rounded-lg p-4 mt-2 space-y-4">
                      <h4 className="font-medium text-sm">Configuration des évaluations</h4>
                      {evalsToShow.map(({ evalConfig, index }) => (
                        <div key={index} className="grid gap-3 p-3 bg-muted/50 rounded-md">
                          <span className="text-sm font-medium">
                            {getTypeEvalLabel(evalConfig.typeEval)}
                          </span>
                          <div className="grid gap-1">
                            <Label className="text-xs">Template</Label>
                            <Select
                              value={evalConfig.template || ""}
                              onValueChange={(v) => handleTemplateChange(index, v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un template" />
                              </SelectTrigger>
                              <SelectContent>
                                {getTemplatesForType(evalConfig.typeEval).map((t) => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              ) : (
                (() => {
                  const currentConfig = getParcoursConfig(parcoursType);
                  const evalsToCustomize = customEvaluations
                    .map((evalConfig, index) => ({
                      evalConfig,
                      index,
                      hasFixedTemplate: !!currentConfig.evaluations[index]?.template,
                    }))
                    .filter(({ hasFixedTemplate }) => !hasFixedTemplate);

                  if (evalsToCustomize.length === 0) return null;

                  return (
                    <>
                      <div className="mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsCustomizing(!isCustomizing)}
                          className="flex items-center gap-2"
                        >
                          {isCustomizing ? (
                            <><ChevronUp className="h-4 w-4" />Masquer la personnalisation</>
                          ) : (
                            <><ChevronDown className="h-4 w-4" />Personnaliser le parcours</>
                          )}
                        </Button>
                      </div>

                      {isCustomizing && (
                        <div className="border rounded-lg p-4 mt-2 space-y-4">
                          <h4 className="font-medium text-sm">Personnalisation des évaluations</h4>
                          {evalsToCustomize.map(({ evalConfig, index }) => (
                            <div key={index} className="grid gap-3 p-3 bg-muted/50 rounded-md">
                              <span className="text-sm font-medium">
                                Évaluation {index + 1} — {getTypeEvalLabel(evalConfig.typeEval)}
                              </span>
                              <div className="grid gap-1">
                                <Label className="text-xs">Template</Label>
                                <Select
                                  value={evalConfig.template || ""}
                                  onValueChange={(v) => handleTemplateChange(index, v)}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getTemplatesForType(evalConfig.typeEval).map((t) => (
                                      <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()
              )}

              {/* Candidats */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>
                    Candidats ({candidats.filter((c) =>
                      anonyme ? c.token : c.emailCandidat && c.nomCandidat && c.prenomCandidat
                    ).length})
                    <span className="text-muted-foreground font-normal ml-1">
                      (optionnel)
                    </span>
                  </Label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      id="csv-quick-upload"
                    />
                    <label htmlFor="csv-quick-upload">
                      <Button type="button" variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-1" />
                          Import CSV
                        </span>
                      </Button>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCsvOpen(true)}
                    >
                      Aperçu CSV
                    </Button>
                  </div>
                </div>

                {candidats.map((c, index) => (
                  <div key={index} className="flex items-end gap-2">
                    {!anonyme && (
                      <div className="grid gap-1 flex-1">
                        <Label className="text-xs">Prénom</Label>
                        <Input
                          value={c.prenomCandidat}
                          onChange={(e) => updateCandidat(index, "prenomCandidat", e.target.value)}
                          placeholder="John"
                        />
                      </div>
                    )}
                    {!anonyme && (
                      <div className="grid gap-1 flex-1">
                        <Label className="text-xs">Nom</Label>
                        <Input
                          value={c.nomCandidat}
                          onChange={(e) => updateCandidat(index, "nomCandidat", e.target.value)}
                          placeholder="Doe"
                        />
                      </div>
                    )}
                    {!anonyme && (
                      <div className="grid gap-1 flex-1">
                        <Label className="text-xs">Email</Label>
                        <Input
                          type="email"
                          value={c.emailCandidat}
                          onChange={(e) => updateCandidat(index, "emailCandidat", e.target.value)}
                          placeholder="john@example.com"
                        />
                      </div>
                    )}
                    {anonyme && (
                      <div className="grid gap-1 flex-1">
                        <Label className="text-xs">Token</Label>
                        <div className="flex gap-1">
                          <Input
                            value={c.token}
                            onChange={(e) => updateCandidat(index, "token", e.target.value)}
                            placeholder="abc-1234"
                          />
                          <Button type="button" variant="outline" size="icon" onClick={() => genererToken(index)}>
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCandidatRow(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCandidatRow}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter un candidat
                </Button>
              </div>

              <Button type="submit" disabled={isLoading} className="mt-2">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  "Créer la campagne"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <CsvImportModal
        isOpen={csvOpen}
        onClose={() => setCsvOpen(false)}
        onImport={handleCsvImport}
      />
    </div>
  );
}
